import { useState, useEffect, useCallback, useMemo, useRef, ComponentType } from 'react';
import toast from 'react-hot-toast';
import {
	Cog6ToothIcon,
	ExclamationTriangleIcon,
	WrenchScrewdriverIcon,
	ClipboardDocumentListIcon,
	// UserGroupIcon, // TODO: Re-enable once Roles & Permissions is ready
	MapPinIcon,
	PlusIcon,
	PencilIcon,
	TrashIcon,
	BuildingStorefrontIcon,
	PhoneIcon,
	EnvelopeIcon,
	MagnifyingGlassIcon,
	CheckIcon,
	ArrowUpTrayIcon,
	ArrowLeftIcon,
	CheckCircleIcon,
	XCircleIcon,
	ExclamationCircleIcon,
	ChevronDownIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Button, ConfirmDialog, Spinner } from '../../components/ui';
import { ServiceModal } from '../../components/providers';
import CsvUploadZone from '../../components/providers/CsvUploadZone';
import AccountTab from '../../components/shared/AccountTab';
import { providerProfileApi, appointmentsApi, ApiError } from '../../api';
import { useLocationModal } from '../../contexts';
import useProviderProfileStore from '../../stores/providerProfileStore';
import useServicesStore from '../../stores/servicesStore';
import type { Service } from '../../stores/servicesStore';
import {
	validateServiceCsvFile,
	validateTaskCsvFile,
	downloadCsvString,
} from '../../utils/csvParser';
import type { ProviderLocation, BulkUploadResponse, BulkUploadStatus } from '../../types/provider';
import type { ServiceTask, TaskPricing, ProviderTask } from '../../types/appointments';

// Tab definitions
type TabId = 'services' | 'tasks' | 'roles' | 'locations' | 'account';

interface Tab {
	id: TabId;
	label: string;
	icon: ComponentType<{ className?: string }>;
}

const TABS: Tab[] = [
	{ id: 'services', label: 'Services', icon: WrenchScrewdriverIcon },
	{ id: 'tasks', label: 'Tasks', icon: ClipboardDocumentListIcon },
	// { id: 'roles', label: 'Roles & Permissions', icon: UserGroupIcon }, // TODO: Re-enable once ready
	{ id: 'locations', label: 'Location Details', icon: MapPinIcon },
	{ id: 'account', label: 'Account', icon: Cog6ToothIcon },
];

// Coming Soon placeholder component
const ComingSoonPlaceholder = ({ tabLabel }: { tabLabel: string }) => (
	<div className="settings-card">
		<div className="py-16 text-center">
			<WrenchScrewdriverIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
			<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
				{tabLabel}
			</h3>
			<p className="text-gray-500 dark:text-gray-400">This section is coming soon.</p>
		</div>
	</div>
);

// Services Tab - Manage salon services and pricing
type ServiceView = 'list' | 'method-chooser' | 'csv-upload';
type CsvStatusType = 'info' | 'success' | 'error' | 'warning' | null;

const ServicesTab = () => {
	const { services, isLoading, error, fetchServices, addService, updateService, deleteService } =
		useServicesStore();
	const { selectedLocationId } = useProviderProfileStore();
	const [searchTerm, setSearchTerm] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingService, setEditingService] = useState<Service | null>(null);
	const [deletingService, setDeletingService] = useState<Service | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// CSV Upload state
	const [currentView, setCurrentView] = useState<ServiceView>('list');
	const [csvStatusMessage, setCsvStatusMessage] = useState<string | null>(null);
	const [csvStatusType, setCsvStatusType] = useState<CsvStatusType>(null);
	const [csvFile, setCsvFile] = useState<File | null>(null);
	const [csvFileInfo, setCsvFileInfo] = useState<{
		serviceCount: number;
		totalRows: number;
	} | null>(null);
	const [isBulkUploading, setIsBulkUploading] = useState(false);
	const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

	// Polling state
	const [uploadStatus, setUploadStatus] = useState<BulkUploadStatus | null>(null);
	const [uploadResult, setUploadResult] = useState<BulkUploadResponse | null>(null);
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// Fetch services when selected location changes
	useEffect(() => {
		if (selectedLocationId) {
			fetchServices(selectedLocationId);
		}
	}, [selectedLocationId, fetchServices]);

	// Clean up polling on unmount
	useEffect(() => {
		return () => {
			if (pollingRef.current) clearInterval(pollingRef.current);
		};
	}, []);

	const stopPolling = useCallback(() => {
		if (pollingRef.current) {
			clearInterval(pollingRef.current);
			pollingRef.current = null;
		}
	}, []);

	const startPolling = useCallback(
		(jobId: number) => {
			stopPolling();
			setUploadStatus('PENDING');
			setUploadResult(null);

			const POLL_INTERVAL = 3000; // 3 seconds

			pollingRef.current = setInterval(async () => {
				try {
					const result = await providerProfileApi.getBulkUploadStatus(jobId);
					setUploadStatus(result.status);
					setUploadResult(result);

					const terminalStatuses: BulkUploadStatus[] = [
						'COMPLETED',
						'FAILED',
						'PARTIALLY_COMPLETED',
					];
					if (terminalStatuses.includes(result.status)) {
						stopPolling();

						if (result.status === 'COMPLETED') {
							toast.success(
								`All ${result.successRows} row(s) uploaded successfully!`
							);
						} else if (result.status === 'PARTIALLY_COMPLETED') {
							toast.success(
								`${result.successRows} row(s) uploaded. ${result.failedRows} row(s) failed.`
							);
						} else {
							toast.error(
								'Bulk upload failed. Please check the errors and try again.'
							);
						}

						// Refresh services list after terminal status
						if (selectedLocationId) {
							await fetchServices(selectedLocationId);
						}
					}
				} catch {
					stopPolling();
					setUploadStatus('FAILED');
					toast.error('Failed to check upload status');
				}
			}, POLL_INTERVAL);
		},
		[selectedLocationId, fetchServices, stopPolling]
	);

	const filteredServices = services.filter(
		service =>
			service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			service.description.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const openAddModal = () => {
		setEditingService(null);
		setCurrentView('method-chooser');
	};

	const handleSelectManual = () => {
		setCurrentView('list');
		setEditingService(null);
		setIsModalOpen(true);
	};

	const handleSelectCsv = () => {
		setCurrentView('csv-upload');
		setCsvStatusMessage(null);
		setCsvStatusType(null);
	};

	const handleBackToList = () => {
		setCurrentView('list');
		setCsvStatusMessage(null);
		setCsvStatusType(null);
		setCsvFile(null);
		setCsvFileInfo(null);
	};

	const handleDownloadTemplate = useCallback(async () => {
		if (!selectedLocationId) return;
		setIsDownloadingTemplate(true);
		try {
			const bytes = await providerProfileApi.downloadServiceTemplate(selectedLocationId);
			downloadCsvString(bytes, 'service_template.csv');
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to download template');
		} finally {
			setIsDownloadingTemplate(false);
		}
	}, [selectedLocationId]);

	const handleCsvFile = useCallback(async (file: File) => {
		setCsvStatusMessage('Validating CSV...');
		setCsvStatusType('info');

		const result = await validateServiceCsvFile(file);

		if (!result.valid) {
			setCsvStatusMessage(result.error || 'Invalid CSV file');
			setCsvStatusType('error');
			setCsvFile(null);
			setCsvFileInfo(null);
			return;
		}

		setCsvFile(file);
		setCsvFileInfo({ serviceCount: result.serviceCount, totalRows: result.totalRows });
		setCsvStatusMessage(
			`Found ${result.serviceCount} service(s) with ${result.totalRows} row(s)`
		);
		setCsvStatusType('success');
	}, []);

	const handleBulkUpload = async () => {
		if (!selectedLocationId || !csvFile) return;

		setIsBulkUploading(true);
		try {
			const result = await providerProfileApi.bulkUploadServices(selectedLocationId, csvFile);

			// File submitted — clear file state
			setCsvFile(null);
			setCsvFileInfo(null);

			// Start polling with the job ID from the response
			toast.success('File uploaded! Processing your services...');
			startPolling(result.id);
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Bulk upload failed');
		} finally {
			setIsBulkUploading(false);
		}
	};

	const handleDismissStatus = () => {
		setUploadStatus(null);
		setUploadResult(null);
		setCurrentView('list');
	};

	const openEditModal = (service: Service) => {
		setEditingService(service);
		setIsModalOpen(true);
	};

	const handleSave = async (service: Service) => {
		if (!selectedLocationId) return;

		try {
			if (editingService) {
				await updateService(selectedLocationId, editingService.id, service);
				toast.success('Service updated successfully!');
			} else {
				await addService(selectedLocationId, service);
				toast.success('Service added successfully!');
			}
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to save service');
			throw err;
		}
	};

	const handleDeleteFromCard = (service: Service) => {
		setDeletingService(service);
	};

	const confirmDelete = async () => {
		if (!deletingService || !selectedLocationId) return;

		setIsDeleting(true);
		try {
			await deleteService(selectedLocationId, deletingService.id);
			toast.success('Service deleted successfully!');
			setDeletingService(null);
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to delete service');
		} finally {
			setIsDeleting(false);
		}
	};

	const headerTitle = currentView === 'list' ? 'Your Services' : 'Add New Service';
	const headerSubtitle =
		currentView === 'list'
			? 'Manage your salon services and pricing'
			: currentView === 'method-chooser'
				? "Choose how you'd like to add your service"
				: 'Upload a CSV file with service details';

	return (
		<>
			<div className="settings-card">
				<div className="settings-card__header">
					<div className="settings-card__icon">
						<WrenchScrewdriverIcon className="w-6 h-6" />
					</div>
					<div className="flex-1">
						<h2 className="settings-card__title">{headerTitle}</h2>
						<p className="settings-card__subtitle">{headerSubtitle}</p>
					</div>
					{currentView === 'list' ? (
						<Button
							variant="primary"
							size="sm"
							onClick={openAddModal}
							className="hidden sm:flex"
						>
							<PlusIcon className="w-4 h-4 mr-1" />
							Add New Service
						</Button>
					) : (
						<Button
							variant="secondary"
							size="sm"
							onClick={handleBackToList}
							className="hidden sm:flex"
						>
							<ArrowLeftIcon className="w-4 h-4 mr-1" />
							Back to List
						</Button>
					)}
				</div>

				<div className="settings-card__body">
					{/* ─── List View ─── */}
					{currentView === 'list' && (
						<>
							{/* Mobile Add Button */}
							<Button
								variant="primary"
								onClick={openAddModal}
								className="w-full mb-4 sm:hidden"
							>
								<PlusIcon className="w-4 h-4 mr-1" />
								Add New Service
							</Button>

							{/* Search Bar */}
							{!isLoading && !error && services.length > 0 && (
								<div className="service-search">
									<MagnifyingGlassIcon className="service-search__icon" />
									<input
										type="text"
										className="service-search__input"
										placeholder="Search services..."
										value={searchTerm}
										onChange={e => setSearchTerm(e.target.value)}
									/>
								</div>
							)}

							{/* Loading State */}
							{isLoading ? (
								<div className="py-12 text-center">
									<Spinner
										size="lg"
										className="mx-auto mb-3 text-purple-600 dark:text-purple-400"
									/>
									<p className="text-sm text-gray-500 dark:text-gray-400">
										Loading services...
									</p>
								</div>
							) : error ? (
								<div className="py-12 text-center">
									<ExclamationTriangleIcon className="w-16 h-16 mx-auto text-red-300 dark:text-red-600 mb-4" />
									<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
										Failed to load services
									</h3>
									<p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
									{selectedLocationId && (
										<Button
											variant="secondary"
											onClick={() => fetchServices(selectedLocationId)}
										>
											Try Again
										</Button>
									)}
								</div>
							) : filteredServices.length === 0 ? (
								<div className="py-12 text-center">
									<WrenchScrewdriverIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
									<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
										{searchTerm ? 'No services found' : 'No Services Yet'}
									</h3>
									<p className="text-gray-500 dark:text-gray-400 mb-4">
										{searchTerm
											? 'Try a different search term'
											: 'Get started by adding your first service.'}
									</p>
									{!searchTerm && (
										<Button variant="primary" onClick={openAddModal}>
											<PlusIcon className="w-4 h-4 mr-1" />
											Add First Service
										</Button>
									)}
								</div>
							) : (
								<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
									{filteredServices.map(service => (
										<div key={service.id} className="service-card">
											<div className="service-card__body">
												<h3 className="service-card__name">
													{service.name}
												</h3>
												{service.description && (
													<p className="service-card__description">
														{service.description}
													</p>
												)}
												<div className="service-card__meta">
													{service.variants.length} combination
													{service.variants.length !== 1 ? 's' : ''}
												</div>
											</div>
											<div className="service-card__footer">
												<button
													onClick={() => openEditModal(service)}
													className="service-card__action service-card__action--edit"
													title="Edit service"
												>
													<PencilIcon className="w-4 h-4" />
													<span>Edit</span>
												</button>
												<button
													onClick={() => handleDeleteFromCard(service)}
													className="service-card__action service-card__action--delete"
													title="Delete service"
												>
													<TrashIcon className="w-4 h-4" />
												</button>
											</div>
										</div>
									))}
								</div>
							)}
						</>
					)}

					{/* ─── Method Chooser View ─── */}
					{currentView === 'method-chooser' && (
						<>
							{/* Mobile Back Button */}
							<Button
								variant="secondary"
								onClick={handleBackToList}
								className="w-full mb-4 sm:hidden"
							>
								<ArrowLeftIcon className="w-4 h-4 mr-1" />
								Back to List
							</Button>

							<div className="method-chooser">
								<div className="method-chooser__grid">
									<button
										className="method-chooser__card"
										onClick={handleSelectManual}
									>
										<PencilIcon className="method-chooser__card-icon" />
										<h3 className="method-chooser__card-title">Manual Entry</h3>
										<p className="method-chooser__card-desc">
											Add service details and pricing combinations one by one
										</p>
										<span className="method-chooser__card-hint">
											Best for single services
										</span>
									</button>

									<button
										className="method-chooser__card"
										onClick={handleSelectCsv}
									>
										<ArrowUpTrayIcon className="method-chooser__card-icon" />
										<h3 className="method-chooser__card-title">CSV Upload</h3>
										<p className="method-chooser__card-desc">
											Import multiple services with all pricing at once via
											spreadsheet
										</p>
										<span className="method-chooser__card-hint">
											Best for bulk import
										</span>
									</button>
								</div>
							</div>
						</>
					)}

					{/* ─── CSV Upload View ─── */}
					{currentView === 'csv-upload' && (
						<>
							{/* Mobile Back Button */}
							{!uploadStatus && (
								<Button
									variant="secondary"
									onClick={handleBackToList}
									className="w-full mb-4 sm:hidden"
								>
									<ArrowLeftIcon className="w-4 h-4 mr-1" />
									Back to List
								</Button>
							)}

							{/* Processing / Result Status */}
							{uploadStatus ? (
								<div className="bulk-upload-status">
									{/* In-progress: PENDING / PROCESSING */}
									{(uploadStatus === 'PENDING' ||
										uploadStatus === 'PROCESSING') && (
										<div className="bulk-upload-status__card bulk-upload-status__card--processing">
											<div className="bulk-upload-status__spinner" />
											<h3 className="bulk-upload-status__title">
												{uploadStatus === 'PENDING'
													? 'Upload Queued'
													: 'Processing Services'}
											</h3>
											<p className="bulk-upload-status__message">
												{uploadStatus === 'PENDING'
													? 'Your file has been submitted and is waiting to be processed...'
													: 'Your services are being created. This may take a moment...'}
											</p>
											{uploadResult && uploadResult.processedRows > 0 && (
												<p className="bulk-upload-status__progress">
													{uploadResult.processedRows} of{' '}
													{uploadResult.totalRows} rows processed
												</p>
											)}
										</div>
									)}

									{/* Terminal: COMPLETED */}
									{uploadStatus === 'COMPLETED' && uploadResult && (
										<div className="bulk-upload-status__card bulk-upload-status__card--success">
											<CheckCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--success" />
											<h3 className="bulk-upload-status__title">
												Upload Complete
											</h3>
											<p className="bulk-upload-status__message">
												All {uploadResult.successRows} row(s) were uploaded
												successfully!
											</p>
											<Button
												variant="primary"
												onClick={handleDismissStatus}
												className="mt-4"
											>
												View Services
											</Button>
										</div>
									)}

									{/* Terminal: PARTIALLY_COMPLETED */}
									{uploadStatus === 'PARTIALLY_COMPLETED' && uploadResult && (
										<div className="bulk-upload-status__card bulk-upload-status__card--warning">
											<ExclamationCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--warning" />
											<h3 className="bulk-upload-status__title">
												Partially Completed
											</h3>
											<p className="bulk-upload-status__message">
												{uploadResult.successRows} row(s) uploaded
												successfully. {uploadResult.failedRows} row(s)
												failed.
											</p>
											{uploadResult.errors.length > 0 && (
												<div className="bulk-upload-status__errors">
													<h4>Errors:</h4>
													<ul>
														{uploadResult.errors.map((err, i) => (
															<li key={i}>
																Row {err.rowIndex}:{' '}
																{err.errorMessage}
															</li>
														))}
													</ul>
												</div>
											)}
											<Button
												variant="primary"
												onClick={handleDismissStatus}
												className="mt-4"
											>
												View Services
											</Button>
										</div>
									)}

									{/* Terminal: FAILED */}
									{uploadStatus === 'FAILED' && (
										<div className="bulk-upload-status__card bulk-upload-status__card--error">
											<XCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--error" />
											<h3 className="bulk-upload-status__title">
												Upload Failed
											</h3>
											<p className="bulk-upload-status__message">
												The bulk upload could not be completed. Please check
												your CSV file and try again.
											</p>
											{uploadResult && uploadResult.errors.length > 0 && (
												<div className="bulk-upload-status__errors">
													<h4>Errors:</h4>
													<ul>
														{uploadResult.errors.map((err, i) => (
															<li key={i}>
																Row {err.rowIndex}:{' '}
																{err.errorMessage}
															</li>
														))}
													</ul>
												</div>
											)}
											<Button
												variant="secondary"
												onClick={handleDismissStatus}
												className="mt-4"
											>
												Back to Services
											</Button>
										</div>
									)}
								</div>
							) : (
								<>
									<CsvUploadZone
										label="CSV Upload"
										description="Bulk-upload service pricing. Each row includes the service name so you can import multiple services at once."
										onFile={handleCsvFile}
										onDownloadTemplate={handleDownloadTemplate}
										isDownloading={isDownloadingTemplate}
										statusMessage={csvStatusMessage}
										statusType={csvStatusType}
										formatGuideItems={[
											'Required columns: serviceId, serviceName, size, length, durationMinutes, price',
											'Optional columns: description, status',
											'Download the template above to see the correct format',
											'You can include multiple services in one CSV file',
										]}
									/>

									{/* File Info + Upload Button */}
									{csvFile && csvFileInfo && (
										<div className="csv-upload__file-info">
											<div className="csv-upload__file-info-details">
												<p className="csv-upload__file-info-name">
													{csvFile.name}
												</p>
												<p className="csv-upload__file-info-meta">
													{csvFileInfo.serviceCount} service(s) &middot;{' '}
													{csvFileInfo.totalRows} row(s)
												</p>
											</div>
											<Button
												variant="primary"
												onClick={handleBulkUpload}
												isLoading={isBulkUploading}
											>
												<ArrowUpTrayIcon className="w-4 h-4 mr-1" />
												Upload Services
											</Button>
										</div>
									)}

									<div className="mt-6">
										<Button variant="secondary" onClick={handleSelectManual}>
											<PencilIcon className="w-4 h-4 mr-1" />
											Switch to Manual Entry
										</Button>
									</div>
								</>
							)}
						</>
					)}
				</div>
			</div>

			<ServiceModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				service={editingService}
				onSave={handleSave}
				onDelete={(id: string) => {
					setIsModalOpen(false);
					const svc = services.find(s => s.id === id);
					if (svc) setDeletingService(svc);
				}}
			/>

			<ConfirmDialog
				isOpen={!!deletingService}
				onClose={() => setDeletingService(null)}
				onConfirm={confirmDelete}
				title="Delete Service"
				message={`Are you sure you want to delete "${deletingService?.name}" and all its pricing combinations? This action cannot be undone.`}
				confirmLabel="Yes, Delete"
				variant="danger"
				isLoading={isDeleting}
			/>
		</>
	);
};

// Task type formatting helpers
const formatTaskType = (type: string) => {
	switch (type) {
		case 'WHOLE':
			return 'Whole';
		case 'FRONT':
			return 'Front';
		case 'BACK':
			return 'Back';
		default:
			return type.charAt(0) + type.slice(1).toLowerCase();
	}
};

const formatSize = (size: string) => {
	const sizeMap: Record<string, string> = {
		XXS: 'XXS',
		XS: 'XS',
		S: 'Small',
		SM: 'SM',
		M: 'Medium',
		L: 'Large',
		XL: 'XL',
		JUMBO: 'Jumbo',
	};
	return sizeMap[size] || size;
};

const formatLength = (length: string) => {
	return length
		.split('_')
		.map(w => w.charAt(0) + w.slice(1).toLowerCase())
		.join(' ');
};

const TASK_TYPE_COLORS: Record<string, string> = {
	WHOLE: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
	FRONT: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
	BACK: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

// CsvStatusType alias (reused by both ServicesTab and TasksTab)
type TaskCsvStatusType = 'info' | 'success' | 'error' | 'warning' | null;

// Tasks Tab - Provider task CRUD + service-task bulk upload + assignments view
const TasksTab = () => {
	const { selectedLocationId } = useProviderProfileStore();

	// Data
	const [providerTasks, setProviderTasks] = useState<ProviderTask[]>([]);
	const [serviceTasks, setServiceTasks] = useState<ServiceTask[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	// Row 1 Left: Add Task
	const [newTaskName, setNewTaskName] = useState('');
	const [newTaskCommission, setNewTaskCommission] = useState(false);
	const [isCreating, setIsCreating] = useState(false);

	// Row 1 Right: Bulk Upload
	const [csvFile, setCsvFile] = useState<File | null>(null);
	const [csvFileInfo, setCsvFileInfo] = useState<{
		serviceCount: number;
		totalRows: number;
	} | null>(null);
	const [csvStatusMessage, setCsvStatusMessage] = useState<string | null>(null);
	const [csvStatusType, setCsvStatusType] = useState<TaskCsvStatusType>(null);
	const [isBulkUploading, setIsBulkUploading] = useState(false);
	const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
	const [uploadStatus, setUploadStatus] = useState<BulkUploadStatus | null>(null);
	const [uploadResult, setUploadResult] = useState<BulkUploadResponse | null>(null);
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// Row 2: All Tasks
	const [taskSearchTerm, setTaskSearchTerm] = useState('');
	const [isTasksCollapsed, setIsTasksCollapsed] = useState(false);
	const [editingTask, setEditingTask] = useState<ProviderTask | null>(null);
	const [editTaskName, setEditTaskName] = useState('');
	const [editTaskCommission, setEditTaskCommission] = useState(false);
	const [isSavingEdit, setIsSavingEdit] = useState(false);
	const [deletingTask, setDeletingTask] = useState<ProviderTask | null>(null);
	const [isDeletingTask, setIsDeletingTask] = useState(false);

	// Row 3: Task Assignments
	const [isAssignmentsCollapsed, setIsAssignmentsCollapsed] = useState(false);
	const [assignmentView, setAssignmentView] = useState<'byService' | 'byTask'>('byService');
	const [deletingAssignment, setDeletingAssignment] = useState<ServiceTask | null>(null);
	const [isDeletingAssignment, setIsDeletingAssignment] = useState(false);

	// ─── Fetch Data ──────────────────────────────────────────────
	const fetchData = useCallback(async () => {
		if (!selectedLocationId) return;
		setIsLoading(true);
		try {
			const [tasks, assignments] = await Promise.all([
				providerProfileApi.getProviderTasks(selectedLocationId),
				appointmentsApi.getServiceTasks(selectedLocationId),
			]);
			setProviderTasks(tasks);
			setServiceTasks(assignments);
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to load tasks');
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// ─── Polling ─────────────────────────────────────────────────
	useEffect(() => {
		return () => {
			if (pollingRef.current) clearInterval(pollingRef.current);
		};
	}, []);

	const stopPolling = useCallback(() => {
		if (pollingRef.current) {
			clearInterval(pollingRef.current);
			pollingRef.current = null;
		}
	}, []);

	const startPolling = useCallback(
		(jobId: number) => {
			stopPolling();
			setUploadStatus('PENDING');
			setUploadResult(null);

			pollingRef.current = setInterval(async () => {
				try {
					const result = await providerProfileApi.getTaskBulkUploadStatus(jobId);
					setUploadStatus(result.status);
					setUploadResult(result);

					const terminalStatuses: BulkUploadStatus[] = [
						'COMPLETED',
						'FAILED',
						'PARTIALLY_COMPLETED',
					];
					if (terminalStatuses.includes(result.status)) {
						stopPolling();
						if (result.status === 'COMPLETED') {
							toast.success(
								`All ${result.successRows} row(s) uploaded successfully!`
							);
						} else if (result.status === 'PARTIALLY_COMPLETED') {
							toast.success(
								`${result.successRows} row(s) uploaded. ${result.failedRows} row(s) failed.`
							);
						} else {
							toast.error(
								'Bulk upload failed. Please check the errors and try again.'
							);
						}
						await fetchData();
					}
				} catch {
					stopPolling();
					setUploadStatus('FAILED');
					toast.error('Failed to check upload status');
				}
			}, 3000);
		},
		[fetchData, stopPolling]
	);

	// ─── Handlers ────────────────────────────────────────────────
	const handleCreateTask = async () => {
		if (!selectedLocationId || !newTaskName.trim()) return;
		setIsCreating(true);
		try {
			await providerProfileApi.createProviderTask(selectedLocationId, newTaskName.trim(), newTaskCommission);
			toast.success('Task created successfully!');
			setNewTaskName('');
			setNewTaskCommission(false);
			await fetchData();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to create task');
		} finally {
			setIsCreating(false);
		}
	};

	const handleTaskCsvFile = useCallback(async (file: File) => {
		setCsvStatusMessage('Validating CSV...');
		setCsvStatusType('info');
		const result = await validateTaskCsvFile(file);
		if (!result.valid) {
			setCsvStatusMessage(result.error || 'Invalid CSV file');
			setCsvStatusType('error');
			setCsvFile(null);
			setCsvFileInfo(null);
			return;
		}
		setCsvFile(file);
		setCsvFileInfo({ serviceCount: result.serviceCount, totalRows: result.totalRows });
		setCsvStatusMessage(
			`Found ${result.serviceCount} task type(s) with ${result.totalRows} row(s)`
		);
		setCsvStatusType('success');
	}, []);

	const handleTaskBulkUpload = async () => {
		if (!selectedLocationId || !csvFile) return;
		setIsBulkUploading(true);
		try {
			const result = await providerProfileApi.bulkUploadServiceTasks(
				selectedLocationId,
				csvFile
			);
			setCsvFile(null);
			setCsvFileInfo(null);
			toast.success('File uploaded! Processing your tasks...');
			startPolling(result.id);
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Bulk upload failed');
		} finally {
			setIsBulkUploading(false);
		}
	};

	const handleDownloadTaskTemplate = useCallback(async () => {
		if (!selectedLocationId) return;
		setIsDownloadingTemplate(true);
		try {
			const csv = await providerProfileApi.downloadTaskTemplate(selectedLocationId);
			downloadCsvString(csv, 'task_template.csv');
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to download template');
		} finally {
			setIsDownloadingTemplate(false);
		}
	}, [selectedLocationId]);

	const handleEditTask = async () => {
		if (!editingTask || !editTaskName.trim()) return;
		setIsSavingEdit(true);
		try {
			await providerProfileApi.updateProviderTask(editingTask.id, editTaskName.trim(), editTaskCommission);
			toast.success('Task updated successfully!');
			setEditingTask(null);
			setEditTaskName('');
			await fetchData();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to update task');
		} finally {
			setIsSavingEdit(false);
		}
	};

	const confirmDeleteTask = async () => {
		if (!deletingTask) return;
		setIsDeletingTask(true);
		try {
			await providerProfileApi.deleteProviderTask(deletingTask.id);
			toast.success('Task deleted successfully!');
			setDeletingTask(null);
			await fetchData();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to delete task');
		} finally {
			setIsDeletingTask(false);
		}
	};

	const confirmDeleteAssignment = async () => {
		if (!deletingAssignment || !selectedLocationId) return;
		setIsDeletingAssignment(true);
		try {
			await appointmentsApi.deleteServiceTask(deletingAssignment.id, selectedLocationId);
			toast.success('Task assignment deleted successfully!');
			setDeletingAssignment(null);
			await fetchData();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to delete assignment');
		} finally {
			setIsDeletingAssignment(false);
		}
	};

	const handleDismissUploadStatus = () => {
		setUploadStatus(null);
		setUploadResult(null);
	};

	// ─── Derived Data ────────────────────────────────────────────
	const filteredProviderTasks = useMemo(
		() =>
			providerTasks.filter(t => t.name.toLowerCase().includes(taskSearchTerm.toLowerCase())),
		[providerTasks, taskSearchTerm]
	);

	const assignmentsByTask = useMemo(() => {
		const map = new Map<string, { serviceTask: ServiceTask; pricing: TaskPricing }[]>();
		for (const st of serviceTasks) {
			for (const tp of st.taskPricing) {
				const key = tp.taskType;
				if (!map.has(key)) map.set(key, []);
				map.get(key)!.push({ serviceTask: st, pricing: tp });
			}
		}
		return map;
	}, [serviceTasks]);

	// Per-task stats: how many services and combos each task has
	const taskStats = useMemo(() => {
		const map = new Map<string, { serviceCount: number; comboCount: number }>();
		for (const task of providerTasks) {
			const taskNameLower = task.name.toLowerCase();
			const serviceIds = new Set<number>();
			let comboCount = 0;
			for (const st of serviceTasks) {
				for (const tp of st.taskPricing) {
					if (tp.taskType.toLowerCase() === taskNameLower) {
						serviceIds.add(st.serviceId);
						comboCount++;
					}
				}
			}
			map.set(task.name, { serviceCount: serviceIds.size, comboCount });
		}
		return map;
	}, [providerTasks, serviceTasks]);

	const hasTasks = providerTasks.length > 0;

	// ─── Render ──────────────────────────────────────────────────
	if (isLoading) {
		return (
			<div className="settings-card">
				<div className="py-12 text-center">
					<Spinner
						size="lg"
						className="mx-auto mb-3 text-purple-600 dark:text-purple-400"
					/>
					<p className="text-sm text-gray-500 dark:text-gray-400">Loading tasks...</p>
				</div>
			</div>
		);
	}

	return (
		<>
			{/* ─── ROW 1: Add Task + Bulk Upload ─── */}
			<div className="tasks-tab__row-1">
				{/* Add New Task */}
				<div className="settings-card">
					<div className="settings-card__header">
						<div className="tasks-tab__icon tasks-tab__icon--purple">
							<PlusIcon className="w-5 h-5 text-white" />
						</div>
						<div className="flex-1">
							<h2 className="settings-card__title">Add New Task</h2>
							<p className="settings-card__subtitle">
								Create task types (e.g. Liner, Braider)
							</p>
						</div>
					</div>
					<div className="settings-card__body">
						<div className="flex gap-3">
							<input
								type="text"
								className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
								placeholder="Task name (e.g. Liner)"
								value={newTaskName}
								onChange={e => setNewTaskName(e.target.value)}
								onKeyDown={e => e.key === 'Enter' && handleCreateTask()}
							/>
							<Button
								variant="primary"
								size="sm"
								onClick={handleCreateTask}
								isLoading={isCreating}
								disabled={!newTaskName.trim()}
							>
								<PlusIcon className="w-4 h-4 mr-1" />
								Create
							</Button>
						</div>
						<label className="flex items-center gap-2 mt-3 cursor-pointer">
							<input
								type="checkbox"
								checked={newTaskCommission}
								onChange={e => setNewTaskCommission(e.target.checked)}
								className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
							/>
							<span className="text-sm text-gray-700 dark:text-gray-300">
								Commission based task
							</span>
						</label>
					</div>
				</div>

				{/* Bulk Upload */}
				<div
					className={`settings-card ${!hasTasks ? 'tasks-tab__bulk-card--disabled' : ''}`}
				>
					<div className="settings-card__header">
						<div className="tasks-tab__icon tasks-tab__icon--green">
							<ArrowUpTrayIcon className="w-5 h-5 text-white" />
						</div>
						<div className="flex-1">
							<h2 className="settings-card__title">Bulk Upload Tasks</h2>
							<p className="settings-card__subtitle">
								{hasTasks
									? 'Import task assignments via CSV'
									: 'Create tasks first to enable bulk upload'}
							</p>
						</div>
					</div>
					<div className="settings-card__body">
						{/* Upload Status (Polling) */}
						{uploadStatus ? (
							<div className="bulk-upload-status">
								{(uploadStatus === 'PENDING' || uploadStatus === 'PROCESSING') && (
									<div className="bulk-upload-status__card bulk-upload-status__card--processing">
										<div className="bulk-upload-status__spinner" />
										<h3 className="bulk-upload-status__title">
											{uploadStatus === 'PENDING'
												? 'Upload Queued'
												: 'Processing Tasks'}
										</h3>
										<p className="bulk-upload-status__message">
											{uploadStatus === 'PENDING'
												? 'Your file has been submitted and is waiting to be processed...'
												: 'Your task assignments are being created. This may take a moment...'}
										</p>
										{uploadResult && uploadResult.processedRows > 0 && (
											<p className="bulk-upload-status__progress">
												{uploadResult.processedRows} of{' '}
												{uploadResult.totalRows} rows processed
											</p>
										)}
									</div>
								)}
								{uploadStatus === 'COMPLETED' && uploadResult && (
									<div className="bulk-upload-status__card bulk-upload-status__card--success">
										<CheckCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--success" />
										<h3 className="bulk-upload-status__title">
											Upload Complete
										</h3>
										<p className="bulk-upload-status__message">
											All {uploadResult.successRows} row(s) were uploaded
											successfully!
										</p>
										<Button
											variant="primary"
											onClick={handleDismissUploadStatus}
											className="mt-4"
										>
											Done
										</Button>
									</div>
								)}
								{uploadStatus === 'PARTIALLY_COMPLETED' && uploadResult && (
									<div className="bulk-upload-status__card bulk-upload-status__card--warning">
										<ExclamationCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--warning" />
										<h3 className="bulk-upload-status__title">
											Partially Completed
										</h3>
										<p className="bulk-upload-status__message">
											{uploadResult.successRows} row(s) uploaded.{' '}
											{uploadResult.failedRows} row(s) failed.
										</p>
										{uploadResult.errors.length > 0 && (
											<div className="bulk-upload-status__errors">
												<h4>Errors:</h4>
												<ul>
													{uploadResult.errors.map((err, i) => (
														<li key={i}>
															Row {err.rowIndex}: {err.errorMessage}
														</li>
													))}
												</ul>
											</div>
										)}
										<Button
											variant="primary"
											onClick={handleDismissUploadStatus}
											className="mt-4"
										>
											Done
										</Button>
									</div>
								)}
								{uploadStatus === 'FAILED' && (
									<div className="bulk-upload-status__card bulk-upload-status__card--error">
										<XCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--error" />
										<h3 className="bulk-upload-status__title">Upload Failed</h3>
										<p className="bulk-upload-status__message">
											The bulk upload could not be completed. Please check
											your CSV and try again.
										</p>
										{uploadResult && uploadResult.errors.length > 0 && (
											<div className="bulk-upload-status__errors">
												<h4>Errors:</h4>
												<ul>
													{uploadResult.errors.map((err, i) => (
														<li key={i}>
															Row {err.rowIndex}: {err.errorMessage}
														</li>
													))}
												</ul>
											</div>
										)}
										<Button
											variant="secondary"
											onClick={handleDismissUploadStatus}
											className="mt-4"
										>
											Try Again
										</Button>
									</div>
								)}
							</div>
						) : (
							<>
								<CsvUploadZone
									label="Task CSV Upload"
									description="Upload task assignments with pricing for your services."
									onFile={handleTaskCsvFile}
									onDownloadTemplate={handleDownloadTaskTemplate}
									isDownloading={isDownloadingTemplate}
									statusMessage={csvStatusMessage}
									statusType={csvStatusType}
									formatGuideItems={[
										'Required columns: serviceTaskId, serviceId, serviceName, taskId, taskName, size, length, durationMinutes, price',
										'Download the template to see the correct format',
										'Each row maps a task to a service with pricing',
									]}
								/>
								{csvFile && csvFileInfo && (
									<div className="csv-upload__file-info">
										<div className="csv-upload__file-info-details">
											<p className="csv-upload__file-info-name">
												{csvFile.name}
											</p>
											<p className="csv-upload__file-info-meta">
												{csvFileInfo.serviceCount} task type(s) &middot;{' '}
												{csvFileInfo.totalRows} row(s)
											</p>
										</div>
										<Button
											variant="primary"
											onClick={handleTaskBulkUpload}
											isLoading={isBulkUploading}
										>
											<ArrowUpTrayIcon className="w-4 h-4 mr-1" />
											Upload Tasks
										</Button>
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</div>

			{/* ─── ROW 2: All Tasks (collapsible) ─── */}
			<div className="settings-card mt-6">
				<div
					className="tasks-tab__section-header"
					onClick={() => setIsTasksCollapsed(!isTasksCollapsed)}
				>
					<div className="tasks-tab__icon tasks-tab__icon--orange">
						<ClipboardDocumentListIcon className="w-5 h-5 text-white" />
					</div>
					<div className="flex-1">
						<h2 className="settings-card__title">All Tasks</h2>
					</div>
					{providerTasks.length > 0 && (
						<span className="tasks-tab__count-badge">{providerTasks.length}</span>
					)}
					{!isTasksCollapsed && providerTasks.length > 3 && (
						<div className="mx-2" onClick={e => e.stopPropagation()}>
							<div className="relative">
								<MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
								<input
									type="text"
									className="pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm w-48 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
									placeholder="Search tasks..."
									value={taskSearchTerm}
									onChange={e => setTaskSearchTerm(e.target.value)}
								/>
							</div>
						</div>
					)}
					<ChevronDownIcon
						className={`w-5 h-5 text-gray-400 transition-transform ${isTasksCollapsed ? '-rotate-90' : ''}`}
					/>
				</div>

				{!isTasksCollapsed && (
					<div className="settings-card__body">
						{providerTasks.length === 0 ? (
							<div className="tasks-tab__empty">
								<ClipboardDocumentListIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
								<p className="text-sm text-gray-500 dark:text-gray-400">
									No tasks yet. Create your first task above.
								</p>
							</div>
						) : filteredProviderTasks.length === 0 ? (
							<div className="tasks-tab__empty">
								<p className="text-sm text-gray-500 dark:text-gray-400">
									No tasks match your search.
								</p>
							</div>
						) : (
							<div className="tasks-tab__task-grid">
								{filteredProviderTasks.map(task => {
									const stats = taskStats.get(task.name) || {
										serviceCount: 0,
										comboCount: 0,
									};
									const isReady = stats.comboCount > 0;
									return (
										<div key={task.id} className="tasks-tab__task-card">
											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2 mb-1">
													<span className="tasks-tab__task-name">
														{task.name}
													</span>
													{task.staffCommission && (
														<span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
															CR
														</span>
													)}
													<span
														className={`tasks-tab__status-dot ${isReady ? 'tasks-tab__status-dot--ready' : 'tasks-tab__status-dot--pending'}`}
													/>
												</div>
												<div className="tasks-tab__task-meta">
													<span>
														{stats.serviceCount} service
														{stats.serviceCount !== 1 ? 's' : ''}
													</span>
													<span className="mx-1.5 text-gray-300 dark:text-gray-600">
														&middot;
													</span>
													<span>
														{stats.comboCount} combo
														{stats.comboCount !== 1 ? 's' : ''}
													</span>
												</div>
												{isReady ? (
													<span className="tasks-tab__status-badge tasks-tab__status-badge--ready">
														Ready
													</span>
												) : (
													<span className="tasks-tab__status-badge tasks-tab__status-badge--pending">
														Needs Setup
													</span>
												)}
											</div>
											<div className="tasks-tab__task-actions">
												<button
													title="Edit task"
													onClick={() => {
														setEditingTask(task);
														setEditTaskName(task.name);
														setEditTaskCommission(task.staffCommission ?? false);
													}}
												>
													<PencilIcon className="w-4 h-4" />
												</button>
												<button
													title="Delete task"
													onClick={() => setDeletingTask(task)}
												>
													<TrashIcon className="w-4 h-4" />
												</button>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				)}
			</div>

			{/* ─── ROW 3: Task Assignments (collapsible) ─── */}
			{serviceTasks.length > 0 && (
				<div className="settings-card mt-6">
					<div
						className="tasks-tab__section-header"
						onClick={() => setIsAssignmentsCollapsed(!isAssignmentsCollapsed)}
					>
						<div className="tasks-tab__icon tasks-tab__icon--purple">
							<BuildingStorefrontIcon className="w-5 h-5 text-white" />
						</div>
						<div className="flex-1">
							<h2 className="settings-card__title">Task Assignments</h2>
						</div>
						{!isAssignmentsCollapsed && (
							<div
								className="tasks-tab__view-toggle"
								onClick={e => e.stopPropagation()}
							>
								<button
									className={`tasks-tab__view-toggle-btn ${assignmentView === 'byService' ? 'tasks-tab__view-toggle-btn--active' : ''}`}
									onClick={() => setAssignmentView('byService')}
								>
									By Service
								</button>
								<button
									className={`tasks-tab__view-toggle-btn ${assignmentView === 'byTask' ? 'tasks-tab__view-toggle-btn--active' : ''}`}
									onClick={() => setAssignmentView('byTask')}
								>
									By Task
								</button>
							</div>
						)}
						<ChevronDownIcon
							className={`w-5 h-5 text-gray-400 transition-transform ${isAssignmentsCollapsed ? '-rotate-90' : ''}`}
						/>
					</div>

					{!isAssignmentsCollapsed && (
						<div className="settings-card__body">
							{/* By Service View */}
							{assignmentView === 'byService' && (
								<div className="space-y-4">
									{serviceTasks.map(st => (
										<div key={st.id} className="tasks-tab__assignment-card">
											<div className="tasks-tab__assignment-header">
												<div className="flex items-center gap-2 flex-1">
													<span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
														{st.serviceName}
													</span>
												</div>
												<button
													className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors cursor-pointer"
													title="Delete assignment"
													onClick={() => setDeletingAssignment(st)}
												>
													<TrashIcon className="w-4 h-4" />
												</button>
											</div>
											<div className="tasks-tab__pricing-group">
												{Object.entries(
													st.taskPricing.reduce<
														Record<string, TaskPricing[]>
													>((acc, tp) => {
														if (!acc[tp.taskType])
															acc[tp.taskType] = [];
														acc[tp.taskType].push(tp);
														return acc;
													}, {})
												).map(([taskType, pricings]) => (
													<div key={taskType} className="mb-3 last:mb-0">
														<span
															className={`tasks-tab__task-badge ${TASK_TYPE_COLORS[taskType] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}
														>
															{formatTaskType(taskType)}
														</span>
														<table className="tasks-tab__pricing-table">
															<thead>
																<tr>
																	<th>Size</th>
																	<th>Length</th>
																	<th>Duration</th>
																	<th>Price</th>
																</tr>
															</thead>
															<tbody>
																{pricings.map((p, idx) => (
																	<tr key={idx}>
																		<td>
																			{formatSize(p.size)}
																		</td>
																		<td>
																			{formatLength(p.length)}
																		</td>
																		<td>
																			{p.durationMinutes} min
																		</td>
																		<td>
																			$
																			{Number(
																				p.price
																			).toFixed(2)}
																		</td>
																	</tr>
																))}
															</tbody>
														</table>
													</div>
												))}
											</div>
										</div>
									))}
								</div>
							)}

							{/* By Task View */}
							{assignmentView === 'byTask' && (
								<div className="space-y-4">
									{Array.from(assignmentsByTask.entries()).map(
										([taskType, entries]) => (
											<div key={taskType} className="tasks-tab__bytask-card">
												<div className="tasks-tab__bytask-header">
													<span
														className={`tasks-tab__task-badge ${TASK_TYPE_COLORS[taskType] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}
													>
														{formatTaskType(taskType)}
													</span>
													<span className="text-xs text-gray-500 dark:text-gray-400">
														{
															new Set(
																entries.map(
																	e => e.serviceTask.serviceName
																)
															).size
														}{' '}
														service(s)
													</span>
												</div>
												<div className="tasks-tab__bytask-body">
													{Array.from(
														entries
															.reduce<
																Map<
																	string,
																	{
																		serviceTask: ServiceTask;
																		pricings: TaskPricing[];
																	}
																>
															>((acc, entry) => {
																const key =
																	entry.serviceTask.serviceName;
																if (!acc.has(key))
																	acc.set(key, {
																		serviceTask:
																			entry.serviceTask,
																		pricings: [],
																	});
																acc.get(key)!.pricings.push(
																	entry.pricing
																);
																return acc;
															}, new Map())
															.entries()
													).map(
														([
															serviceName,
															{ serviceTask, pricings },
														]) => (
															<div
																key={serviceName}
																className="tasks-tab__bytask-service"
															>
																<div className="flex items-center gap-2 mb-2">
																	<span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
																		{serviceName}
																	</span>
																	<span className="tasks-tab__method-badge">
																		{serviceTask.method}
																	</span>
																</div>
																<table className="tasks-tab__pricing-table">
																	<thead>
																		<tr>
																			<th>Size</th>
																			<th>Length</th>
																			<th>Duration</th>
																			<th>Price</th>
																		</tr>
																	</thead>
																	<tbody>
																		{pricings.map((p, idx) => (
																			<tr key={idx}>
																				<td>
																					{formatSize(
																						p.size
																					)}
																				</td>
																				<td>
																					{formatLength(
																						p.length
																					)}
																				</td>
																				<td>
																					{
																						p.durationMinutes
																					}{' '}
																					min
																				</td>
																				<td>
																					$
																					{Number(
																						p.price
																					).toFixed(2)}
																				</td>
																			</tr>
																		))}
																	</tbody>
																</table>
															</div>
														)
													)}
												</div>
											</div>
										)
									)}
								</div>
							)}
						</div>
					)}
				</div>
			)}

			{/* ─── Edit Task Modal ─── */}
			{editingTask && (
				<div className="tasks-tab__edit-modal-overlay" onClick={() => setEditingTask(null)}>
					<div className="tasks-tab__edit-modal" onClick={e => e.stopPropagation()}>
						<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
							Rename Task
						</h3>
						<input
							type="text"
							className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm mb-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
							value={editTaskName}
							onChange={e => setEditTaskName(e.target.value)}
							onKeyDown={e => e.key === 'Enter' && handleEditTask()}
							autoFocus
						/>
						<label className="flex items-center gap-2 mb-4 cursor-pointer">
							<input
								type="checkbox"
								checked={editTaskCommission}
								onChange={e => setEditTaskCommission(e.target.checked)}
								className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
							/>
							<span className="text-sm text-gray-700 dark:text-gray-300">
								Commission based task
							</span>
						</label>
						<div className="flex justify-end gap-3">
							<Button
								variant="secondary"
								size="sm"
								onClick={() => setEditingTask(null)}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								size="sm"
								onClick={handleEditTask}
								isLoading={isSavingEdit}
								disabled={
									!editTaskName.trim() || (editTaskName.trim() === editingTask.name && editTaskCommission === (editingTask.staffCommission ?? false))
								}
							>
								<CheckIcon className="w-4 h-4 mr-1" />
								Save
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* ─── Delete Task Confirm ─── */}
			<ConfirmDialog
				isOpen={!!deletingTask}
				onClose={() => setDeletingTask(null)}
				onConfirm={confirmDeleteTask}
				title="Delete Task"
				message={`Are you sure you want to delete "${deletingTask?.name}"? This action cannot be undone.`}
				confirmLabel="Yes, Delete"
				variant="danger"
				isLoading={isDeletingTask}
			/>

			{/* ─── Delete Assignment Confirm ─── */}
			<ConfirmDialog
				isOpen={!!deletingAssignment}
				onClose={() => setDeletingAssignment(null)}
				onConfirm={confirmDeleteAssignment}
				title="Delete Task Assignment"
				message={`Are you sure you want to delete the task assignment for "${deletingAssignment?.serviceName}"? This will remove all pricing for this assignment.`}
				confirmLabel="Yes, Delete"
				variant="danger"
				isLoading={isDeletingAssignment}
			/>
		</>
	);
};

// Helper to format location address
const formatAddress = (location: ProviderLocation): string => {
	const parts = [location.address, location.city, location.state, location.zipCode].filter(
		Boolean
	);
	return parts.join(', ');
};

// Locations Tab - Location List with Add/Edit Modal
const LocationsTab = () => {
	const { profile, fetchProfile } = useProviderProfileStore();
	const { openAddLocation, openEditLocation } = useLocationModal();
	const [deletingLocation, setDeletingLocation] = useState<ProviderLocation | null>(null);
	const [isDeletingLocation, setIsDeletingLocation] = useState(false);

	const locations = profile?.locations || [];

	const handleEditLocation = (location: ProviderLocation) => {
		openEditLocation(location);
	};

	const handleDeleteLocation = (location: ProviderLocation) => {
		if (location.primary) {
			toast.error(
				'Cannot delete the primary location. Please set another location as primary first.'
			);
			return;
		}
		setDeletingLocation(location);
	};

	const confirmDeleteLocation = async () => {
		if (!deletingLocation) return;

		setIsDeletingLocation(true);
		try {
			await providerProfileApi.deleteLocation(deletingLocation.id);
			toast.success('Location deleted successfully!');
			fetchProfile();
		} catch (error) {
			const apiError = error as ApiError;
			toast.error(apiError.message || 'Failed to delete location');
		} finally {
			setIsDeletingLocation(false);
			setDeletingLocation(null);
		}
	};

	return (
		<>
			{/* Your Locations Card */}
			<div className="settings-card">
				<div className="settings-card__header">
					<div className="settings-card__icon">
						<MapPinIcon className="w-6 h-6" />
					</div>
					<div className="flex-1">
						<h2 className="settings-card__title">Your Locations</h2>
						<p className="settings-card__subtitle">Manage your salon locations</p>
					</div>
					<Button
						variant="primary"
						size="sm"
						onClick={openAddLocation}
						className="hidden sm:flex"
					>
						<PlusIcon className="w-4 h-4 mr-1" />
						Add Location
					</Button>
				</div>

				<div className="settings-card__body">
					{/* Mobile Add Button */}
					<Button
						variant="primary"
						onClick={openAddLocation}
						className="w-full mb-4 sm:hidden"
					>
						<PlusIcon className="w-4 h-4 mr-1" />
						Add Location
					</Button>

					{/* Locations Grid */}
					{locations.length === 0 ? (
						<div className="py-12 text-center">
							<BuildingStorefrontIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
							<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
								No Locations Yet
							</h3>
							<p className="text-gray-500 dark:text-gray-400 mb-4">
								Add your first salon location to get started.
							</p>
						</div>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{locations.map(location => (
								<div key={location.id} className="location-card">
									<div className="location-card__header">
										<div className="location-card__badges">
											{location.primary && (
												<span className="location-card__badge location-card__badge--primary">
													Primary
												</span>
											)}
											<span
												className={`location-card__badge ${
													location.active
														? 'location-card__badge--active'
														: 'location-card__badge--inactive'
												}`}
											>
												{location.active ? 'Active' : 'Inactive'}
											</span>
										</div>
										<div className="location-card__actions">
											<button
												onClick={() => handleEditLocation(location)}
												className="location-card__edit"
												title="Edit location"
											>
												<PencilIcon className="w-4 h-4" />
											</button>
											<button
												onClick={() => handleDeleteLocation(location)}
												className="location-card__delete"
												title={
													location.primary
														? 'Cannot delete primary location'
														: 'Delete location'
												}
												disabled={location.primary}
											>
												<TrashIcon className="w-4 h-4" />
											</button>
										</div>
									</div>

									<div className="location-card__address">
										<MapPinIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
										<span>{formatAddress(location)}</span>
									</div>

									{(location.phone || location.email) && (
										<div className="location-card__contact">
											{location.phone && (
												<div className="location-card__contact-item">
													<PhoneIcon className="w-4 h-4 text-gray-400" />
													<span>{location.phone}</span>
												</div>
											)}
											{location.email && (
												<div className="location-card__contact-item">
													<EnvelopeIcon className="w-4 h-4 text-gray-400" />
													<span>{location.email}</span>
												</div>
											)}
										</div>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			<ConfirmDialog
				isOpen={!!deletingLocation}
				onClose={() => setDeletingLocation(null)}
				onConfirm={confirmDeleteLocation}
				title="Delete Location"
				message={`Are you sure you want to delete this location?\n\n${deletingLocation ? formatAddress(deletingLocation) : ''}`}
				confirmLabel="Yes, Delete"
				variant="danger"
				isLoading={isDeletingLocation}
			/>
		</>
	);
};

const Settings = () => {
	const [activeTab, setActiveTab] = useState<TabId>('services');

	const renderTabContent = () => {
		switch (activeTab) {
			case 'account':
				return <AccountTab />;
			case 'services':
				return <ServicesTab />;
			case 'tasks':
				return <TasksTab />;
			case 'roles':
				return <ComingSoonPlaceholder tabLabel="Roles & Permissions" />;
			case 'locations':
				return <LocationsTab />;
			default:
				return null;
		}
	};

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
					Settings
				</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					Manage your account and business settings
				</p>
			</div>

			{/* Settings Tabs */}
			<div className="settings-tabs">
				{TABS.map(tab => (
					<button
						key={tab.id}
						className={`settings-tab ${activeTab === tab.id ? 'settings-tab--active' : ''}`}
						onClick={() => setActiveTab(tab.id)}
					>
						<tab.icon className="w-4 h-4 md:hidden" />
						<span className="hidden md:inline">{tab.label}</span>
						<span className="md:hidden">{tab.label.split(' ')[0]}</span>
					</button>
				))}
			</div>

			{/* Tab Content */}
			<div className="settings-content">{renderTabContent()}</div>
		</AppLayout>
	);
};

export default Settings;
